import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Card, CardBody, CardHeader, Row, Col, ListGroup, ListGroupItem, Badge, Button } from "reactstrap";
import { ClipLoader } from "react-spinners";
import apis from "../../../../Api";

function StudentHistoryModal({
  isOpen,
  onClose,
  studentId,
  companyId,
  onOpenPresenceHistory,
  onViewStudentProgress,
  formatShortDate,
  formatDateTime,
  formatDuration
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [student, setStudent] = useState(null);

  useEffect(() => {
    let mounted = true;

    const safeRequest = async (promise) => {
      try {
        const resp = await promise;
        return { data: resp?.data !== undefined ? resp.data : resp };
      } catch (err) {
        console.error('StudentHistoryModal request error:', err);
        return { error: err };
      }
    };

    const load = async () => {
      if (!isOpen) return;
      if (!studentId) {
        setData(null);
        setStudent(null);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setData(null);
      setStudent(null);

      try {
        const [
          presenceResult,
          tasksResult,
          classroomsResult,
          progressResult,
          usersCompanyResult
        ] = await Promise.all([
          safeRequest(apis.getCompanyPresenceHistory(companyId, studentId)),
          safeRequest(apis.getUserTasksWithDetails(studentId)),
          safeRequest(apis.getUserClassrooms(studentId)),
          safeRequest(apis.getUserProgressDetail(studentId)),
          companyId ? safeRequest(apis.getUsersCompany(companyId)) : Promise.resolve({ data: [] })
        ]);

        const presencePayload = presenceResult?.data || {};
        const presenceRecordsRaw = Array.isArray(presencePayload.records) ? presencePayload.records : [];
        const presenceSummaryRaw = presencePayload.summary || {};
        const presenceSummary = {
          total: Number(presenceSummaryRaw.total) || presenceRecordsRaw.length || 0,
          present: Number(presenceSummaryRaw.present) || 0,
          absent: Number(presenceSummaryRaw.absent) || 0,
          lastDate: presenceSummaryRaw.lastDate || null,
          lastStatus: presenceSummaryRaw.lastStatus || null,
          lastDateFormatted: presenceSummaryRaw.lastDateFormatted || null
        };
        const presenceRecords = presenceRecordsRaw.slice(0, 10);

        const tasksRaw = Array.isArray(tasksResult?.data) ? tasksResult.data : [];
        const classroomsRaw = Array.isArray(classroomsResult?.data) ? classroomsResult.data : [];
        const progressPayload = progressResult?.data || {};
        const progressClassrooms = Array.isArray(progressPayload.classrooms) ? progressPayload.classrooms : [];
        const progressSummaryRaw = progressPayload.summary || {};

        // build classroom and unit name maps
        const classroomNameMap = new Map();
        (classroomsRaw || []).forEach(cls => {
          if (cls && cls.id !== undefined && cls.id !== null) {
            classroomNameMap.set(String(cls.id), cls.name || `Turma ${cls.id}`);
          }
        });
        const unitNameMap = new Map();
        (progressClassrooms || []).forEach(cls => {
          const units = Array.isArray(cls.units) ? cls.units : [];
          units.forEach(unit => {
            if (unit && unit.id !== undefined && unit.id !== null) {
              unitNameMap.set(String(unit.id), unit.name || `Unidade ${unit.id}`);
            }
          });
        });

        const uniqueTaskIds = [...new Set((tasksRaw || []).map(item => item?.task).filter(id => id !== undefined && id !== null))];
        const taskDetailsMap = new Map();
        if (uniqueTaskIds.length > 0) {
          const taskDetailResponses = await Promise.all(
            uniqueTaskIds.map(taskId => safeRequest(apis.getTask(taskId)).then(result => ({ taskId, result })))
          );
          taskDetailResponses.forEach(({ taskId, result }) => {
            const detail = result?.data;
            if (detail && detail.id !== undefined && detail.id !== null) {
              taskDetailsMap.set(String(detail.id), detail);
              if (detail.unit !== undefined && detail.unit !== null && !unitNameMap.has(String(detail.unit))) {
                unitNameMap.set(String(detail.unit), `Unidade ${detail.unit}`);
              }
            }
          });
        }

        const tasksByClassroomMap = new Map();
        const taskStatusCounts = {};
        let latestTaskMoment = null;

        (tasksRaw || []).forEach(taskItem => {
          const statusKey = (taskItem?.status || "entregue").toString().toLowerCase();
          taskStatusCounts[statusKey] = (taskStatusCounts[statusKey] || 0) + 1;

          if (taskItem?.sendDate) {
            const parsed = require('moment')(taskItem.sendDate);
            if (parsed.isValid() && (!latestTaskMoment || parsed.isAfter(latestTaskMoment))) {
              latestTaskMoment = parsed;
            }
          }

          const detail = taskDetailsMap.get(String(taskItem?.task));
          const classroomId = detail?.class_room ?? detail?.classRoom ?? null;
          const classroomKey = classroomId !== null && classroomId !== undefined ? String(classroomId) : `unknown-${taskItem?.id || taskItem?.task}`;
          const classroomName = classroomNameMap.get(classroomKey) || (classroomId ? `Turma ${classroomId}` : "Turma não identificada");

          if (!tasksByClassroomMap.has(classroomKey)) {
            tasksByClassroomMap.set(classroomKey, {
              classroomId,
              classroomName,
              total: 0,
              units: new Map(),
              tasks: []
            });
          }

          const entry = tasksByClassroomMap.get(classroomKey);
          entry.total += 1;

          const unitId = detail?.unit ?? null;
          if (unitId !== null && unitId !== undefined) {
            const unitKey = String(unitId);
            const unitName = unitNameMap.get(unitKey) || `Unidade ${unitId}`;
            const currentUnit = entry.units.get(unitKey) || { unitId, unitName, count: 0 };
            currentUnit.count += 1;
            entry.units.set(unitKey, currentUnit);
          }

          entry.tasks.push({
            id: taskItem?.id,
            name: detail?.name || `Tarefa ${taskItem?.task}`,
            sendDate: taskItem?.sendDate || null,
            status: taskItem?.status || null,
            unitId: detail?.unit ?? null,
            unitName: detail?.unit ? (unitNameMap.get(String(detail.unit)) || `Unidade ${detail.unit}`) : null
          });
        });

        const tasksByClassroom = Array.from(tasksByClassroomMap.values()).map(item => ({
          ...item,
          units: Array.from(item.units.values()),
          tasks: item.tasks
            .slice()
            .sort((a, b) => {
              const aDate = a.sendDate ? require('moment')(a.sendDate) : null;
              const bDate = b.sendDate ? require('moment')(b.sendDate) : null;
              if (aDate && bDate) return bDate.valueOf() - aDate.valueOf();
              if (aDate) return -1;
              if (bDate) return 1;
              return (a.name || "").localeCompare(b.name || "");
            })
        })).sort((a, b) => b.total - a.total);

        const progressSummary = {
          totalSeconds: Number(progressSummaryRaw.totalSeconds) || 0,
          totalMinutes: Number(progressSummaryRaw.totalMinutes) || Math.round((Number(progressSummaryRaw.totalSeconds) || 0) / 60),
          lessonsCount: Number(progressSummaryRaw.lessonsCount) || 0,
          sessionsCount: Number(progressSummaryRaw.sessionsCount) || 0,
          classroomsCount: progressClassrooms.length
        };

        const progressAggregate = (Array.isArray(progressClassrooms) ? progressClassrooms : []).reduce((acc, classroom) => {
          const summary = classroom?.summary || {};
          acc.totalSeconds += Number(summary.totalSeconds) || 0;
          acc.totalLessons += Number(summary.lessons) || 0;
          if (Array.isArray(classroom?.units)) {
            acc.totalUnits += classroom.units.length;
          }
          const last = summary.lastActivity;
          if (last) {
            const lastMoment = require('moment')(last);
            if (lastMoment.isValid() && (!acc.lastActivity || lastMoment.isAfter(acc.lastActivity))) {
              acc.lastActivity = lastMoment;
            }
          }
          return acc;
        }, { totalSeconds: 0, totalLessons: 0, totalUnits: 0, lastActivity: null });

        const progressSummaryEnriched = {
          ...progressSummary,
          totalUnits: progressAggregate.totalUnits,
          totalLessons: progressAggregate.totalLessons,
          lastActivity: progressAggregate.lastActivity ? progressAggregate.lastActivity.format("DD/MM/YYYY HH:mm") : null
        };

        const processedData = {
          presence: {
            summary: presenceSummary,
            records: presenceRecords,
            totalRecords: presenceRecordsRaw.length
          },
          tasks: {
            total: tasksRaw.length,
            statusCounts: taskStatusCounts,
            lastDelivery: latestTaskMoment ? latestTaskMoment.format("DD/MM/YYYY HH:mm") : null,
            perClassroom: tasksByClassroom
          },
          classrooms: {
            total: classroomsRaw.length,
            list: classroomsRaw
          },
          progress: {
            summary: progressSummaryEnriched,
            classrooms: progressClassrooms
          }
        };

        // find student basic profile from company users if possible
        const usersList = usersCompanyResult?.data || [];
        const foundUser = usersList.find(u => String(u.id) === String(studentId));
        const profile = foundUser ? {
          id: foundUser.id,
          name: foundUser.name,
          lastName: foundUser.lastName,
          email: foundUser.email,
          photo: foundUser.photo || "/images/defaultAvatar.png",
          roleLabel: foundUser.roleLabel || "Aluno",
          statusLabel: foundUser.statusLabel || (foundUser.isBlocked ? "Bloqueado" : "Ativo"),
          isBlocked: Boolean(foundUser.isBlocked),
          progressSummary: {
            totalSeconds: progressSummaryEnriched.totalSeconds,
            totalMinutes: progressSummaryEnriched.totalMinutes,
            lessonsCount: progressSummaryEnriched.lessonsCount,
            sessionsCount: progressSummaryEnriched.sessionsCount,
            lastActivity: progressSummaryEnriched.lastActivity
          }
        } : {
          id: studentId,
          name: "Aluno",
          lastName: "",
          email: "",
          photo: "/images/defaultAvatar.png",
          roleLabel: "Aluno",
          statusLabel: null,
          isBlocked: false,
          progressSummary: {
            totalSeconds: progressSummaryEnriched.totalSeconds,
            totalMinutes: progressSummaryEnriched.totalMinutes,
            lessonsCount: progressSummaryEnriched.lessonsCount,
            sessionsCount: progressSummaryEnriched.sessionsCount,
            lastActivity: progressSummaryEnriched.lastActivity
          }
        };

        if (!mounted) return;
        setData(processedData);
        setStudent(profile);
      } catch (err) {
        console.error('Erro ao carregar histórico completo do aluno (modal):', err);
        if (!mounted) return;
        setError("Não foi possível carregar o histórico completo deste aluno.");
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };

    load();

    return () => { mounted = false; };
  }, [isOpen, studentId, companyId]);

  if (!isOpen || !student) return null;

  const presenceSummary = data?.presence?.summary || { total: 0, present: 0, absent: 0, lastDate: null, lastStatus: null, lastDateFormatted: null };
  const presenceRecords = data?.presence?.records || [];
  const tasksInfo = data?.tasks || { total: 0, statusCounts: {}, lastDelivery: null, perClassroom: [] };
  const classroomsInfo = data?.classrooms || { total: 0, list: [] };
  const progressInfo = data?.progress || { summary: { totalSeconds: 0, totalMinutes: 0, lessonsCount: 0, sessionsCount: 0, classroomsCount: 0, totalUnits: 0, totalLessons: 0, lastActivity: null }, classrooms: [] };

  const formatStatusLabel = status => {
    if (!status) return "Sem status";
    const normalized = status.toString().replace(/_/g, " ");
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const totalStudyTimeLabel = formatDuration(progressInfo.summary.totalSeconds);
  const lastActivityLabel = progressInfo.summary.lastActivity || "-";
  const presenceNeutral = Math.max((presenceSummary.total || 0) - ((presenceSummary.present || 0) + (presenceSummary.absent || 0)), 0);

  const summaryTiles = [
    { key: "present", label: "Presenças", value: presenceSummary.present || 0, icon: "fas fa-user-check", color: "bg-gradient-success", description: "Registros de presença" },
    { key: "absent", label: "Faltas", value: presenceSummary.absent || 0, icon: "fas fa-user-times", color: "bg-gradient-danger", description: "Ausências registradas" },
    { key: "tasks", label: "Tarefas enviadas", value: tasksInfo.total || 0, icon: "fas fa-tasks", color: "bg-gradient-info", description: "Entregas realizadas" },
    { key: "study", label: "Tempo estudado", value: totalStudyTimeLabel, icon: "fas fa-stopwatch", color: "bg-gradient-primary", description: "Tempo total registrado" }
  ];

  const hasPresenceRecords = presenceRecords.length > 0;
  const hasTasks = tasksInfo.total > 0;
  const hasClassrooms = classroomsInfo.total > 0;
  const hasProgressByClassroom = Array.isArray(progressInfo.classrooms) && progressInfo.classrooms.length > 0;
  const lastDeliveryLabel = tasksInfo.lastDelivery || "-";
  const presenceRecentCount = presenceRecords.length;

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" className="modal-dialog-scrollable" style={{ maxWidth: "90%", maxHeight: "88vh" }}>
      <ModalHeader toggle={onClose}>Histórico de {student.name} {student.lastName}</ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="text-center py-5">
            <ClipLoader size={32} color="#5e72e4" />
          </div>
        ) : null}

        {!loading && error ? (
          <div className="alert alert-danger" role="alert">{error}</div>
        ) : null}

        {!loading && !error ? (
          <>
            <Card className="mb-4 border-0 shadow-sm">
              <CardBody className="p-4">
                <Row className="align-items-center">
                  <Col md="4" className="d-flex align-items-center mb-3 mb-md-0">
                    <div className="mr-3" style={{ width: "80px", height: "80px", borderRadius: "50%", overflow: "hidden", boxShadow: "0 0 0 3px rgba(255,255,255,0.2)" }}>
                      <img src={student.photo || "/images/defaultAvatar.png"} alt={student.name || "Aluno"} className="rounded-circle" style={{ width: "80px", height: "80px", objectFit: "cover" }} />
                    </div>
                    <div>
                      <div className="h3 mb-1">{student.name} {student.lastName}</div>
                      <div className="text-sm text-white-50">{student.email || "Sem e-mail"}</div>
                      <div className="text-sm text-white-50">ID {student.id}</div>
                      <div className="mt-3 d-flex align-items-center flex-wrap">
                        <Badge color={student.isBlocked ? "danger" : "success"} pill>
                          {student.statusLabel || (student.isBlocked ? "Bloqueado" : "Ativo")}
                        </Badge>
                        <Badge color="light" pill className="text-primary ml-2 mt-2 mt-md-0">
                          {student.roleLabel || "Aluno"}
                        </Badge>
                      </div>
                    </div>
                  </Col>
                  <Col md="8">
                    <Row className="text-center">
                      <Col sm="6" md="3" className="mb-3">
                        <div className="text-uppercase text-white-50 small">Último status</div>
                        <div className="h4 mb-0">{presenceSummary.lastStatus ? formatStatusLabel(presenceSummary.lastStatus) : "--"}</div>
                      </Col>
                      <Col sm="6" md="3" className="mb-3">
                        <div className="text-uppercase text-white-50 small">Última presença</div>
                        <div className="h4 mb-0">{presenceSummary.lastDateFormatted || "-"}</div>
                      </Col>
                      <Col sm="6" md="3" className="mb-3">
                        <div className="text-uppercase text-white-50 small">Última entrega</div>
                        <div className="h4 mb-0">{lastDeliveryLabel}</div>
                      </Col>
                      <Col sm="6" md="3" className="mb-3">
                        <div className="text-uppercase text-white-50 small">Última atividade</div>
                        <div className="h4 mb-0">{lastActivityLabel}</div>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Row className="mb-4">
              {summaryTiles.map(tile => (
                <Col sm="6" md="3" key={tile.key} className="mb-3">
                  <Card className="shadow-sm border-0 h-100">
                    <CardBody className="py-3">
                      <div className="d-flex align-items-center">
                        <div className={`icon icon-shape ${tile.color} text-white rounded-circle shadow`}>
                          <i className={tile.icon}></i>
                        </div>
                        <div className="ml-3">
                          <div className="text-muted text-uppercase small">{tile.label}</div>
                          <div className="h4 font-weight-bold mb-0">{tile.value}</div>
                          {tile.description ? <div className="text-muted small mt-1">{tile.description}</div> : null}
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>

            <Card className="mb-4 shadow-sm border-0">
              <CardHeader className="bg-transparent d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-uppercase text-muted ls-1 mb-1">Frequência</h6>
                  <h5 className="h3 mb-0">Presenças e faltas</h5>
                  <div className="text-sm text-muted">Total de {presenceSummary.total || 0} registros · Última presença em {presenceSummary.lastDateFormatted || "-"}</div>
                </div>
                <div className="d-flex align-items-center">
                  <Badge color="info" pill className="mr-3">{presenceRecentCount} recentes</Badge>
                  <Button size="sm" color="primary" outline onClick={() => onOpenPresenceHistory && onOpenPresenceHistory(student)}>Ver histórico completo</Button>
                </div>
              </CardHeader>
              <CardBody>
                <Row className="text-center mb-4">
                  <Col xs="6" md="3" className="mb-3">
                    <div className="text-uppercase text-muted small">Registros</div>
                    <div className="h4 mb-0">{presenceSummary.total || 0}</div>
                  </Col>
                  <Col xs="6" md="3" className="mb-3">
                    <div className="text-uppercase text-muted small">Presenças</div>
                    <div className="h4 text-success mb-0">{presenceSummary.present || 0}</div>
                  </Col>
                  <Col xs="6" md="3" className="mb-3">
                    <div className="text-uppercase text-muted small">Faltas</div>
                    <div className="h4 text-danger mb-0">{presenceSummary.absent || 0}</div>
                  </Col>
                  <Col xs="6" md="3" className="mb-3">
                    <div className="text-uppercase text-muted small">Sem registro</div>
                    <div className="h4 mb-0">{presenceNeutral}</div>
                  </Col>
                </Row>
                {hasPresenceRecords ? (
                  <ListGroup className="list-group-flush" flush>
                    {presenceRecords.map(record => {
                      const type = (record.type || "").toLowerCase();
                      const badgeColor = type === "presente" ? "success" : type === "falta" ? "danger" : "secondary";
                      return (
                        <ListGroupItem key={record.id || `${record.date}-${record.classroomId}`} className="d-flex justify-content-between align-items-center py-3 border-0 shadow-sm mb-3 rounded" style={{ backgroundColor: "#f8f9fe" }}>
                          <div>
                            <div className="font-weight-bold text-dark">{formatShortDate(record.date)}</div>
                            <div className="small text-muted d-flex align-items-center flex-wrap">
                              {record.classroomName ? <Badge color="light" className="text-primary mr-2 mb-1">{record.classroomName}</Badge> : null}
                              <span>{record.lessonName || record.unitName || "Registro manual"}</span>
                            </div>
                          </div>
                          <Badge color={badgeColor} pill className="px-3 text-capitalize">{type || "--"}</Badge>
                        </ListGroupItem>
                      );
                    })}
                  </ListGroup>
                ) : (
                  <div className="text-center text-muted py-3">Nenhum registro recente encontrado.</div>
                )}
              </CardBody>
            </Card>

            <Card className="mb-4 shadow-sm border-0">
              <CardHeader className="bg-transparent d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-uppercase text-muted ls-1 mb-1">Entregas</h6>
                  <h5 className="h3 mb-0">Tarefas enviadas</h5>
                  <div className="text-sm text-muted">Última entrega em {lastDeliveryLabel}</div>
                </div>
                <Badge color="info" pill>{tasksInfo.total}</Badge>
              </CardHeader>
              <CardBody>
                {hasTasks ? (
                  <>
                    <Row className="mb-4 text-center">
                      {Object.entries(tasksInfo.statusCounts).map(([status, total]) => (
                        <Col key={status} xs="6" md="3" className="mb-3">
                          <div className="text-uppercase text-muted small">{formatStatusLabel(status)}</div>
                          <div className="h5 mb-0">{total}</div>
                        </Col>
                      ))}
                    </Row>
                    <ListGroup className="list-group-flush" flush>
                      {tasksInfo.perClassroom.map(entry => (
                        <ListGroupItem key={entry.classroomId || entry.classroomName} className="py-3 border-0 shadow-sm mb-3 rounded">
                          <div className="d-flex justify-content-between align-items-start flex-wrap">
                            <div className="mr-3">
                              <div className="font-weight-bold text-dark">{entry.classroomName}</div>
                              <div className="small text-muted">{entry.total} entrega(s)</div>
                              {entry.units.length > 0 ? (
                                <div className="mt-2">
                                  {entry.units.map(unit => (
                                    <Badge key={`${entry.classroomId || entry.classroomName}-${unit.unitId || unit.unitName}`} color="light" className="text-primary mr-2 mb-2 border border-primary">
                                      {unit.unitName} ({unit.count})
                                    </Badge>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                            <Badge color="info" pill className="mb-2">{entry.total}</Badge>
                          </div>
                          {entry.tasks.length > 0 ? (
                            <div className="mt-3">
                              {entry.tasks.slice(0, 3).map(task => {
                                const sendLabel = formatDateTime(task.sendDate) || formatShortDate(task.sendDate);
                                return (
                                  <div key={task.id || `${entry.classroomId}-${task.name}`} className="small text-muted d-flex align-items-center mb-1">
                                    <i className="far fa-check-circle text-success mr-2"></i>
                                    <span>{task.name}</span>
                                    {task.unitName ? <span className="ml-2">· {task.unitName}</span> : null}
                                    {sendLabel ? <span className="ml-2">· {sendLabel}</span> : null}
                                    {task.status ? <span className="ml-2">· {formatStatusLabel(task.status)}</span> : null}
                                  </div>
                                );
                              })}
                              {entry.tasks.length > 3 ? (
                                <div className="small text-muted">+ {entry.tasks.length - 3} outras entregas nesta turma</div>
                              ) : null}
                            </div>
                          ) : null}
                        </ListGroupItem>
                      ))}
                    </ListGroup>
                  </>
                ) : (
                  <div className="text-center text-muted py-3">Nenhuma entrega registrada para este aluno.</div>
                )}
              </CardBody>
            </Card>

            <Card className="mb-4 shadow-sm border-0">
              <CardHeader className="bg-transparent d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-uppercase text-muted ls-1 mb-1">Turmas</h6>
                  <h5 className="h3 mb-0">Participações ativas</h5>
                </div>
                <Badge color="primary" pill>{classroomsInfo.total}</Badge>
              </CardHeader>
              <CardBody>
                {hasClassrooms ? (
                  <ListGroup className="list-group-flush" flush>
                    {classroomsInfo.list.map(classroom => {
                      const courseLabel = classroom.courseName || (classroom.course ? `Curso ${classroom.course}` : "Turma");
                      return (
                        <ListGroupItem key={classroom.id} className="py-3 border-0 shadow-sm mb-3 rounded">
                          <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                              <div className="font-weight-bold text-dark">{classroom.name}</div>
                              <div className="small text-muted">Início: {formatShortDate(classroom.openingDate)} · Fim: {formatShortDate(classroom.closingDate)}</div>
                            </div>
                            <Badge color="secondary" pill className="mt-2 mt-md-0">{courseLabel}</Badge>
                          </div>
                        </ListGroupItem>
                      );
                    })}
                  </ListGroup>
                ) : (
                  <div className="text-center text-muted py-3">Nenhuma turma ativa encontrada para este aluno.</div>
                )}
              </CardBody>
            </Card>

            <Card className="mb-4 shadow-sm border-0">
              <CardHeader className="bg-transparent d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-uppercase text-muted ls-1 mb-1">Engajamento</h6>
                  <h5 className="h3 mb-0">Progresso consolidado</h5>
                  <div className="text-sm text-muted">Unidades cursadas: {progressInfo.summary.totalUnits || 0} · Sessões registradas: {progressInfo.summary.sessionsCount || 0}</div>
                </div>
                <Button size="sm" color="primary" outline onClick={() => onViewStudentProgress && onViewStudentProgress(student)}>Ver progresso detalhado</Button>
              </CardHeader>
              <CardBody>
                <Row className="text-center mb-4">
                  <Col xs="6" md="3" className="mb-3">
                    <div className="text-uppercase text-muted small">Tempo estudado</div>
                    <div className="h5 mb-0">{totalStudyTimeLabel}</div>
                  </Col>
                  <Col xs="6" md="3" className="mb-3">
                    <div className="text-uppercase text-muted small">Sessões</div>
                    <div className="h5 mb-0">{progressInfo.summary.sessionsCount}</div>
                  </Col>
                  <Col xs="6" md="3" className="mb-3">
                    <div className="text-uppercase text-muted small">Aulas distintas</div>
                    <div className="h5 mb-0">{progressInfo.summary.lessonsCount}</div>
                  </Col>
                  <Col xs="6" md="3" className="mb-3">
                    <div className="text-uppercase text-muted small">Última atividade</div>
                    <div className="h5 mb-0">{lastActivityLabel}</div>
                  </Col>
                </Row>
                {hasProgressByClassroom ? (
                  <ListGroup className="list-group-flush" flush>
                    {progressInfo.classrooms.map(classroom => {
                      const summary = classroom.summary || {};
                      const units = Array.isArray(classroom.units) ? classroom.units.length : 0;
                      return (
                        <ListGroupItem key={classroom.id} className="py-3 border-0 shadow-sm mb-3 rounded">
                          <div className="d-flex justify-content-between align-items-start flex-wrap">
                            <div className="mr-3">
                              <div className="font-weight-bold text-dark">{classroom.name || "Turma"}</div>
                              <div className="small text-muted">{summary.lessons || 0} aulas · {formatDuration(summary.totalSeconds || 0)} · {units} unidade(s)</div>
                              <div className="small text-muted mt-1">Última atividade: {summary.lastActivity ? formatDateTime(summary.lastActivity) : "-"}</div>
                            </div>
                            <Badge color="info" pill className="mt-2 mt-md-0">{formatDuration(summary.totalSeconds || 0)}</Badge>
                          </div>
                        </ListGroupItem>
                      );
                    })}
                  </ListGroup>
                ) : (
                  <div className="text-center text-muted py-3">Nenhum progresso registrado ainda.</div>
                )}
              </CardBody>
            </Card>
          </>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>Fechar</Button>
      </ModalFooter>
    </Modal>
  );
}

export default StudentHistoryModal;
